import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  LogOut, Bookmark, BookmarkCheck, MessageSquareText, ListChecks, Plus, Loader2,
  ShieldCheck, MapPin, Sparkles, TrendingUp, Award, ArrowUpRight, Trash2, Send,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, logout, authHeaders } = useAuth();
  const nav = useNavigate();
  if (user?.role === "admin") { nav("/admin"); return null; }

  return (
    <div className="min-h-screen bg-brand-paper" data-testid="dashboard-page">
      <header className="bg-brand-paper/95 backdrop-blur border-b border-brand-line sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/"><img src="/yuukke-logo.png" alt="Yuukke" className="h-8 w-auto" /></Link>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-wine border-l border-brand-line pl-3">
              {user?.role === "buyer" ? "Buyer" : "Business"}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-brand-muted hidden md:inline" data-testid="dashboard-user-email">{user?.email}</span>
            <button onClick={async () => { await logout(); nav("/"); }} className="flex items-center gap-2 text-sm text-brand-wine hover:text-brand-wineDark" data-testid="dashboard-logout">
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {user?.role === "buyer" ? (
          <BuyerDashboard user={user} authHeaders={authHeaders} />
        ) : (
          <BusinessDashboard user={user} authHeaders={authHeaders} />
        )}
      </main>
    </div>
  );
}

/* ================= BUYER DASHBOARD ================= */
function BuyerDashboard({ user, authHeaders }) {
  const [tab, setTab] = useState("shortlist");
  return (
    <div>
      <span className="eyebrow" data-testid="buyer-dashboard-eyebrow">Buyer Console</span>
      <h1 className="mt-4 font-serif text-5xl md:text-6xl font-medium tracking-[-0.01em] leading-[1.05] text-brand-ink">
        Welcome, <span className="italic text-gradient-signature">{user.name?.split(" ")[0] || "friend"}</span>
      </h1>
      <p className="mt-3 text-brand-muted text-[15px] max-w-lg">Manage your shortlist, sourcing requests, and intro conversations.</p>

      <div className="mt-10 flex items-center gap-2 border-b border-brand-line" data-testid="buyer-tabs">
        {[
          { key: "shortlist", label: "Shortlist", icon: Bookmark },
          { key: "rfps", label: "Sourcing RFPs", icon: ListChecks },
          { key: "intros", label: "Intro Requests", icon: MessageSquareText },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 px-4 pb-4 pt-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === key ? "text-brand-wine border-brand-wine" : "text-brand-muted border-transparent hover:text-brand-ink"
            }`}
            data-testid={`buyer-tab-${key}`}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "shortlist" && <ShortlistView authHeaders={authHeaders} />}
        {tab === "rfps" && <RfpView authHeaders={authHeaders} />}
        {tab === "intros" && <IntrosView authHeaders={authHeaders} />}
      </div>
    </div>
  );
}

function ShortlistView({ authHeaders }) {
  const [state, setState] = useState({ loading: true, businesses: [] });

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await axios.get(`${API}/me/shortlist`, { headers: authHeaders() });
      setState({ loading: false, businesses: res.data.businesses || [] });
    } catch { setState({ loading: false, businesses: [] }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/me/shortlist/${id}`, { headers: authHeaders() });
      toast.success("Removed from shortlist");
      load();
    } catch { toast.error("Failed to remove"); }
  };

  if (state.loading) return <SkeletonCards />;
  if (!state.businesses.length) return (
    <EmptyState
      icon={<Bookmark size={22} className="text-brand-wine" />}
      title="No businesses shortlisted yet"
      body="Explore the Business Exchange and tap the bookmark icon to save businesses here."
      cta={<Link to="/#businesses" className="btn-solid" data-testid="empty-explore-btn">Explore businesses</Link>}
      testid="shortlist-empty"
    />
  );

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shortlist-grid">
      {state.businesses.map((b) => (
        <BusinessCard key={b.id} b={b} onRemove={() => remove(b.id)} />
      ))}
    </div>
  );
}

function BusinessCard({ b, onRemove }) {
  return (
    <motion.article initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="bg-brand-cream border border-brand-line rounded-2xl overflow-hidden card-lift"
      data-testid={`shortlist-card-${b.id}`}
    >
      <Link to={`/business/${b.id}`} className="block">
        <div className="relative aspect-[4/3] overflow-hidden">
          <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
          {b.verified && (
            <div className="absolute top-3 left-3 bg-brand-cream/95 backdrop-blur rounded-full px-2.5 py-1 flex items-center gap-1 text-[11px] font-semibold text-brand-wine">
              <ShieldCheck size={12} /> Verified
            </div>
          )}
          {b.readiness_score != null && (
            <div className="absolute bottom-3 left-3 bg-brand-cream/95 rounded-full px-2.5 py-1 text-[11px] font-semibold text-brand-ink flex items-center gap-1">
              <Sparkles size={11} className="text-brand-wine" /> {b.readiness_score}/100
            </div>
          )}
        </div>
        <div className="p-5">
          <span className="text-[10px] uppercase tracking-widest text-brand-muted">{b.tag}</span>
          <h3 className="mt-1 font-serif text-xl font-semibold text-brand-ink">{b.name}</h3>
          <p className="mt-2 text-sm text-brand-muted line-clamp-2">{b.tagline || b.description}</p>
          <div className="mt-3 flex items-center gap-1.5 text-xs text-brand-muted"><MapPin size={12} />{b.location}</div>
        </div>
      </Link>
      <div className="p-4 pt-0 flex gap-2">
        <button onClick={onRemove} className="text-xs text-brand-muted hover:text-brand-wine flex items-center gap-1" data-testid={`shortlist-remove-${b.id}`}>
          <Trash2 size={13} /> Remove
        </button>
      </div>
    </motion.article>
  );
}

/* --------- RFP tab --------- */
const categories = [
  "Handmade & Artisan", "Corporate Gifting", "Home & Living", "Fashion & Accessories",
  "Beauty & Wellness", "Food & Beverage", "Sustainable Products", "Office & Workplace",
  "Manufacturing", "D2C Brands", "Consultants", "Marketing & Branding",
  "Finance & Accounting", "Legal Services", "Technology & IT", "Creative Services",
];

function RfpView({ authHeaders }) {
  const [rfps, setRfps] = useState(null);
  const [openNew, setOpenNew] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/me/rfps`, { headers: authHeaders() });
      setRfps(res.data);
    } catch { setRfps([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (rfps === null) return <SkeletonCards />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-brand-muted">
          {rfps.length} active {rfps.length === 1 ? "request" : "requests"}. Each RFP is auto-matched with best-fit suppliers.
        </p>
        <button onClick={() => setOpenNew(true)} className="btn-solid !py-2.5 !px-4" data-testid="new-rfp-btn">
          <Plus size={15} /> New sourcing request
        </button>
      </div>

      {rfps.length === 0 ? (
        <EmptyState
          icon={<ListChecks size={22} className="text-brand-wine" />}
          title="No sourcing requests yet"
          body="Post your first RFP and Yuukke will surface the best-matched women-led businesses instantly."
          cta={<button onClick={() => setOpenNew(true)} className="btn-solid" data-testid="empty-rfp-btn"><Plus size={15}/> Post an RFP</button>}
          testid="rfp-empty"
        />
      ) : (
        <div className="space-y-6" data-testid="rfp-list">
          {rfps.map((r) => (
            <div key={r.id} className="bg-brand-cream border border-brand-line rounded-2xl p-6 md:p-8" data-testid={`rfp-card-${r.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <span className="eyebrow" style={{ fontSize: 11 }}>{r.category || "General"}</span>
                  <h3 className="mt-2 font-serif text-2xl font-semibold text-brand-ink">{r.title}</h3>
                  <p className="mt-2 text-sm text-brand-muted max-w-2xl">{r.description}</p>
                  <div className="mt-3 flex flex-wrap gap-4 text-xs text-brand-muted">
                    {r.city && <span className="flex items-center gap-1"><MapPin size={11} />{r.city}</span>}
                    {r.budget && <span>Budget: {r.budget}</span>}
                    {r.timeline && <span>Timeline: {r.timeline}</span>}
                    {r.quantity && <span>Qty: {r.quantity}</span>}
                  </div>
                </div>
                <span className="text-[11px] font-semibold uppercase tracking-widest bg-brand-blush text-brand-wine border border-brand-wine/20 rounded-full px-3 py-1">
                  {r.matches?.length || 0} matches
                </span>
              </div>

              {r.matches?.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-xs uppercase tracking-widest text-brand-muted mb-3">AI-matched suppliers</h4>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`rfp-matches-${r.id}`}>
                    {r.matches.slice(0, 6).map((m) => (
                      <Link to={`/business/${m.id}`} key={m.id} className="flex items-center gap-3 bg-brand-paper2/60 border border-brand-line rounded-lg p-3 hover:border-brand-wine/40 transition-colors">
                        <img src={m.image} alt={m.name} className="w-12 h-12 rounded-lg object-cover" />
                        <div className="min-w-0">
                          <p className="font-serif text-[15px] font-semibold text-brand-ink truncate">{m.name}</p>
                          <p className="text-[11px] text-brand-muted truncate">{m.tag} · {m.location}</p>
                        </div>
                        <ArrowUpRight size={14} className="ml-auto text-brand-wine shrink-0" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <NewRfpDialog open={openNew} onOpenChange={setOpenNew} authHeaders={authHeaders} onCreated={load} />
    </div>
  );
}

function NewRfpDialog({ open, onOpenChange, authHeaders, onCreated }) {
  const [form, setForm] = useState({ title: "", category: "", city: "", budget: "", timeline: "", quantity: "", description: "" });
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.length < 3) return toast.error("Enter a title");
    if (form.description.length < 10) return toast.error("Describe what you need");
    setBusy(true);
    try {
      const res = await axios.post(`${API}/me/rfps`, form, { headers: authHeaders() });
      toast.success(`RFP posted · ${res.data.matches?.length || 0} matched suppliers`);
      setForm({ title: "", category: "", city: "", budget: "", timeline: "", quantity: "", description: "" });
      onOpenChange(false);
      onCreated?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to post RFP"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-brand-paper border-brand-line p-0 overflow-hidden max-h-[90vh]" data-testid="new-rfp-dialog">
        <div className="p-6 md:p-8 overflow-y-auto max-h-[90vh]">
          <DialogHeader>
            <span className="eyebrow">Sourcing RFP</span>
            <DialogTitle className="mt-2 font-serif text-3xl font-medium text-brand-ink">Post a sourcing request</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} noValidate className="mt-6 space-y-4" data-testid="new-rfp-form">
            <FField label="Title *"><FInput value={form.title} onChange={(e) => u("title", e.target.value)} placeholder="e.g. Corporate gifting for Diwali" testid="rfp-title" /></FField>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FField label="Category">
                <Select value={form.category} onValueChange={(v) => u("category", v)}>
                  <SelectTrigger className="bg-brand-cream border-brand-line rounded-lg h-[46px]" data-testid="rfp-category"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent className="bg-brand-cream max-h-72">
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FField>
              <FField label="City"><FInput value={form.city} onChange={(e) => u("city", e.target.value)} placeholder="Mumbai" testid="rfp-city" /></FField>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FField label="Budget"><FInput value={form.budget} onChange={(e) => u("budget", e.target.value)} placeholder="₹5–10L" testid="rfp-budget" /></FField>
              <FField label="Timeline"><FInput value={form.timeline} onChange={(e) => u("timeline", e.target.value)} placeholder="4 weeks" testid="rfp-timeline" /></FField>
              <FField label="Quantity"><FInput value={form.quantity} onChange={(e) => u("quantity", e.target.value)} placeholder="500 units" testid="rfp-qty" /></FField>
            </div>
            <FField label="Describe what you need *">
              <textarea value={form.description} onChange={(e) => u("description", e.target.value)} rows={4}
                placeholder="Scope, must-haves, certifications, MOQ, delivery expectations…"
                className="w-full bg-brand-cream border border-brand-line rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-wine/20 focus:border-brand-wine transition-all resize-none"
                data-testid="rfp-description" />
            </FField>
            <button type="submit" disabled={busy} className="w-full bg-brand-wine text-white rounded-lg py-4 font-semibold hover:bg-brand-wineDark transition-all flex items-center justify-center gap-2 disabled:opacity-70" data-testid="rfp-submit">
              {busy ? <><Loader2 size={16} className="animate-spin" /> Matching suppliers…</> : <><Send size={15} /> Post RFP & find matches</>}
            </button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* --------- Intro Requests tab --------- */
function IntrosView({ authHeaders }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    axios.get(`${API}/me/intro-requests`, { headers: authHeaders() })
      .then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [authHeaders]);
  if (items === null) return <SkeletonCards />;
  if (!items.length) return (
    <EmptyState
      icon={<MessageSquareText size={22} className="text-brand-wine" />}
      title="No intro requests yet"
      body="Tap 'Request an intro' on any business detail page to start a conversation."
      cta={<Link to="/#businesses" className="btn-solid">Browse businesses</Link>}
      testid="intros-empty"
    />
  );
  return (
    <div className="space-y-4" data-testid="intros-list">
      {items.map((r) => (
        <div key={r.id} className="bg-brand-cream border border-brand-line rounded-2xl p-6 flex items-start justify-between gap-4 flex-wrap" data-testid={`intro-${r.id}`}>
          <div>
            <span className="text-[11px] uppercase tracking-widest text-brand-muted">Sent {new Date(r.created_at).toLocaleDateString()}</span>
            <h3 className="mt-1 font-serif text-xl font-semibold text-brand-ink">{r.business_name}</h3>
            {r.message && <p className="mt-2 text-sm text-brand-muted max-w-xl">"{r.message}"</p>}
          </div>
          <span className="text-[11px] font-semibold uppercase tracking-widest text-brand-wine bg-brand-blush border border-brand-wine/20 rounded-full px-3 py-1">
            {r.status}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ================= BUSINESS DASHBOARD ================= */
function BusinessDashboard({ user, authHeaders }) {
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/me/application`, { headers: authHeaders() });
      setApp(res.data);
    } catch { setApp(false); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Poll for AI while pending
  useEffect(() => {
    if (app && app.ai_status === "pending") {
      const t = setInterval(load, 4000);
      return () => clearInterval(t);
    }
  }, [app]); // eslint-disable-line

  if (loading) return <SkeletonCards />;
  if (!app) return <div className="text-brand-muted">No application linked to this account.</div>;

  const ai = app.ai || {};

  return (
    <div>
      <span className="eyebrow" data-testid="business-dashboard-eyebrow">Business Console</span>
      <h1 className="mt-4 font-serif text-5xl md:text-6xl font-medium tracking-[-0.01em] leading-[1.05] text-brand-ink">
        {app.business_name}
      </h1>
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <StatusPill status={app.status} />
        <span className="text-sm text-brand-muted">{app.category} · {app.city || "India"}</span>
      </div>

      {ai.tagline && (
        <p className="mt-6 font-serif italic text-2xl md:text-3xl text-gradient-signature max-w-3xl leading-snug">
          &ldquo;{ai.tagline}&rdquo;
        </p>
      )}

      <div className="mt-10 grid lg:grid-cols-3 gap-6" data-testid="business-summary">
        <SummaryCard label="Procurement Readiness"
          value={app.ai_status === "ready" ? `${ai.readiness_score}/100` : app.ai_status === "pending" ? "Generating…" : "—"}
          hint={app.ai_status === "ready" ? ai.readiness_band : app.ai_status === "pending" ? "Claude Sonnet 4.5 is analyzing" : "Retry needed"}
          highlight
        />
        <SummaryCard label="Status" value={statusLabel(app.status)} hint={app.status === "approved" ? "Live on the Exchange" : app.status === "rejected" ? "Please contact curation" : "Awaiting curation review"} />
        <SummaryCard label="AI Profile" value={app.ai_status === "ready" ? "Ready" : app.ai_status === "pending" ? "In progress" : "Failed"} hint={app.ai_status === "ready" ? "Standardized profile generated" : "Auto-generated by Yuukke AI"} />
      </div>

      {app.ai_status === "pending" && (
        <div className="mt-8 bg-brand-blush border border-brand-wine/15 rounded-2xl p-6 flex items-center gap-4">
          <Loader2 className="animate-spin text-brand-wine" size={22} />
          <div>
            <p className="font-serif text-xl text-brand-ink">Your AI-generated profile is on the way</p>
            <p className="text-sm text-brand-muted">Usually 10–20 seconds. This page refreshes automatically.</p>
          </div>
        </div>
      )}

      {app.ai_status === "ready" && (
        <div className="mt-10 grid lg:grid-cols-2 gap-6">
          <div className="bg-brand-cream border border-brand-line rounded-2xl p-6 md:p-8">
            <h3 className="eyebrow"><Sparkles size={12} /> Elevator Pitch</h3>
            <p className="mt-4 text-brand-ink leading-relaxed">{ai.elevator_pitch}</p>
            {ai.capabilities?.length > 0 && (
              <div className="mt-6">
                <h4 className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-muted mb-2">Capabilities</h4>
                <div className="flex flex-wrap gap-2">
                  {ai.capabilities.map((c) => <span key={c} className="text-sm bg-brand-paper2 border border-brand-line rounded-full px-3 py-1">{c}</span>)}
                </div>
              </div>
            )}
            {ai.buyer_keywords?.length > 0 && (
              <div className="mt-6">
                <h4 className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-muted mb-2">Buyer keywords</h4>
                <div className="flex flex-wrap gap-2">
                  {ai.buyer_keywords.map((k) => <span key={k} className="text-xs text-brand-wine bg-brand-wine/5 border border-brand-wine/15 rounded-full px-3 py-1">#{k}</span>)}
                </div>
              </div>
            )}
          </div>
          <div className="space-y-6">
            <div className="bg-brand-cream border border-brand-line rounded-2xl p-6 md:p-8">
              <h3 className="eyebrow"><TrendingUp size={12} /> Recommendations</h3>
              <ol className="mt-4 space-y-2 text-[14px] text-brand-ink list-decimal pl-5">
                {(ai.recommendations || []).map((r, i) => <li key={i}>{r}</li>)}
              </ol>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-brand-cream border border-brand-line rounded-2xl p-5">
                <h4 className="text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-2">Strengths</h4>
                <ul className="space-y-1.5 text-sm text-brand-ink">
                  {(ai.strengths || []).map((s, i) => <li key={i}>▲ {s}</li>)}
                </ul>
              </div>
              <div className="bg-brand-cream border border-brand-line rounded-2xl p-5">
                <h4 className="text-xs uppercase tracking-widest text-amber-700 font-semibold mb-2">Gaps</h4>
                <ul className="space-y-1.5 text-sm text-brand-ink">
                  {(ai.gaps || []).map((g, i) => <li key={i}>△ {g}</li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, hint, highlight }) {
  return (
    <div className={`rounded-2xl border p-6 ${highlight ? "bg-brand-wine text-brand-cream border-brand-wine" : "bg-brand-cream border-brand-line"}`}>
      <div className={`text-xs uppercase tracking-widest ${highlight ? "text-brand-cream/70" : "text-brand-muted"}`}>{label}</div>
      <div className="mt-2 font-serif text-3xl md:text-4xl font-medium">{value}</div>
      <div className={`mt-1 text-xs ${highlight ? "text-brand-cream/80" : "text-brand-muted"}`}>{hint}</div>
    </div>
  );
}
function StatusPill({ status }) {
  const map = {
    pending_review: "bg-amber-50 text-amber-800 border-amber-200",
    approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rejected: "bg-rose-50 text-rose-800 border-rose-200",
  };
  return <span className={`text-[11px] uppercase tracking-widest font-semibold rounded-full px-3 py-1 border ${map[status] || "bg-brand-paper2 text-brand-muted border-brand-line"}`}>{statusLabel(status)}</span>;
}
function statusLabel(s) { return s === "pending_review" ? "Pending review" : s === "approved" ? "Approved" : s === "rejected" ? "Rejected" : s; }

/* --------- shared helpers --------- */
function SkeletonCards() {
  return <div className="grid md:grid-cols-3 gap-6">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-52 bg-brand-cream border border-brand-line rounded-2xl animate-pulse" />)}</div>;
}
function EmptyState({ icon, title, body, cta, testid }) {
  return (
    <div className="bg-brand-cream border border-brand-line rounded-2xl p-10 md:p-16 text-center" data-testid={testid}>
      <div className="w-14 h-14 rounded-full bg-brand-blush flex items-center justify-center mx-auto mb-4">{icon}</div>
      <h3 className="font-serif text-2xl font-semibold text-brand-ink">{title}</h3>
      <p className="mt-2 text-sm text-brand-muted max-w-md mx-auto">{body}</p>
      <div className="mt-6 inline-flex">{cta}</div>
    </div>
  );
}
function FField({ label, children }) { return <label className="block"><span className="block text-[13px] font-medium text-brand-ink mb-1.5">{label}</span>{children}</label>; }
function FInput({ testid, ...props }) { return <input {...props} data-testid={testid} className="w-full bg-brand-cream border border-brand-line rounded-lg px-4 py-3 h-[46px] focus:outline-none focus:ring-2 focus:ring-brand-wine/20 focus:border-brand-wine transition-all" />; }
